import apiClient from '@/lib/api';
import type {
  EntityCashFlow,
  AssembledStatement,
  ValidationBoard,
} from '@/types';

// Get the ECF (with lines) for entity+period
export async function getECF(entityId: string, period: string): Promise<EntityCashFlow> {
  const { data } = await apiClient.get(`/cashflows/entity/${entityId}/period/${period}`);
  return data.ecf ?? data;
}

// Submit an entity's cash flow (IN_PROGRESS → SUBMITTED)
export async function submitECF(entityId: string, period: string): Promise<void> {
  await apiClient.post(`/cashflows/entity/${entityId}/period/${period}/submit`);
}

// Agree an entity's cash flow (SUBMITTED → AGREED)
export async function agreeECF(entityId: string, period: string): Promise<void> {
  await apiClient.post(`/cashflows/entity/${entityId}/period/${period}/agree`);
}

// Revert an entity's cash flow (SUBMITTED → IN_PROGRESS)
export async function revertECF(entityId: string, period: string): Promise<void> {
  await apiClient.post(`/cashflows/entity/${entityId}/period/${period}/revert`);
}

// Reopen an agreed cash flow (AGREED → IN_PROGRESS)
export async function reopenECF(entityId: string, period: string): Promise<void> {
  await apiClient.post(`/cashflows/entity/${entityId}/period/${period}/reopen`);
}

// Get the assembled statement for entity+period, plus the calling user's
// per-entity capabilities (computed server-side from their assignments).
export type StatementPermissions = { canPrepare: boolean; canReview: boolean };

export async function assembleStatement(
  entityId: string,
  period: string,
): Promise<AssembledStatement & { permissions: StatementPermissions }> {
  const { data } = await apiClient.get(`/statement/entity/${entityId}/period/${period}`);
  const statement = data.statement ?? data;
  // Default to permissive if an older server build omits permissions.
  const permissions: StatementPermissions =
    data.permissions ?? { canPrepare: true, canReview: true };
  return { ...statement, permissions };
}

// Division-level consolidation
export async function getDivisionConsolidation(
  divisionId: string,
  period: string,
): Promise<AssembledStatement> {
  const { data } = await apiClient.get(`/statement/division/${divisionId}/period/${period}`);
  return data.statement ?? data;
}

// Group-level consolidation
export async function getGroupConsolidation(period: string): Promise<AssembledStatement> {
  const { data } = await apiClient.get(`/statement/group/period/${period}`);
  return data.statement ?? data;
}

// Validation board (all entities x periods)
export async function getValidationBoard(period?: string): Promise<ValidationBoard> {
  const params = period ? { period } : {};
  const { data } = await apiClient.get('/validation-board', { params });
  return data.board ?? data;
}
